import React from 'react';
import { Dialog } from '@material-ui/core';

// CMP Accelerator Member Dashboard (2) pop-up frame (ACC:1311-1347): 520px box,
// 16px radius, red round close, 76px icon circle, 16px/600 title, 14px body.
// Used by the CMP member pages only; CPA pages keep their own dialogs.

export const CMP_DESIGN_RED = '#ED1C24';
export const CMP_DESIGN_GREEN = '#10B981';
export const CMP_DELETE_BANNER_UPDATE_NOTE = 'Click Update at the bottom of the page after deleting this banner to apply the change to the banner in our Chamber Perks App™ and website directories';
const GOLD = '#E1A731';
const GOLD_DARK = '#C6912A';
const BLACK_900 = '#1A1A1A';

const TrashIcon = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <path d="M4 7h16M9 7V5h6v2M6.5 7l1 13h9l1-13M10 11v6M14 11v6" />
  </svg>
);
const SmallTrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M4 7h16M9 7V5h6v2M6.5 7l1 13h9l1-13" />
  </svg>
);
const MailIcon = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="6" y="6" width="15" height="12" rx="2" /><path d="M6.5 7.5l7 5.5 7-5.5" /><path d="M2 10h3M3 13.5h2" />
  </svg>
);

export const CMP_DESIGN_ICONS = {
  trash: <TrashIcon size={34} />,
  mail: <MailIcon />,
};

type Action = { label: string; onClick: () => void; kind: 'danger' | 'cancel' | 'gold'; testId?: string };

const buttonStyle = (kind: Action['kind'], width: number | string): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10, border: 'none',
  borderRadius: kind === 'gold' ? 8 : 10, padding: kind === 'gold' ? '13px 0' : '14px 0', width,
  fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 600, cursor: 'pointer', color: '#FFFFFF',
  background: kind === 'danger' ? CMP_DESIGN_RED : kind === 'cancel' ? BLACK_900 : GOLD,
});

export const CmpDesignDialog = ({
  open, onClose, icon, iconBg, title, lines, actions, actionWidth = 180, testId,
}: {
  open: boolean;
  onClose: () => void;
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  lines: React.ReactNode[];
  actions: Action[];
  /** Width of each button; "100%" for a single full-width button. */
  actionWidth?: number | string;
  testId?: string;
}) => (
  <Dialog open={open} onClose={onClose} maxWidth={false}
    PaperProps={{ style: { borderRadius: 16, width: 'min(520px, 94vw)', margin: 0, boxShadow: '0 8px 30px rgba(0,0,0,.12)' } }}
    BackdropProps={{ style: { background: 'rgba(0,0,0,0.5)' } }}>
    <div data-testid={testId} role="document" style={{
      padding: '28px 36px 32px', boxSizing: 'border-box', position: 'relative', display: 'flex',
      flexDirection: 'column', alignItems: 'center', gap: 18, fontFamily: "'DM Sans', sans-serif",
    }}>
      <button type="button" onClick={onClose} aria-label="Close" style={{
        position: 'absolute', top: 14, right: 14, width: 28, height: 28, borderRadius: '50%', background: CMP_DESIGN_RED,
        color: '#FFFFFF', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 0,
      }}>✕</button>
      <div style={{ width: 76, height: 76, borderRadius: '50%', background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 8 }}>
        {icon}
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, color: '#000000', textAlign: 'center' }}>{title}</div>
      {lines.map((line, index) => (
        <div key={index} style={{ fontSize: 14, color: '#000000', textAlign: 'center', lineHeight: 1.8 }}>{line}</div>
      ))}
      <div style={{ display: 'flex', gap: 14, marginTop: 6, width: actionWidth === '100%' ? '100%' : undefined }}>
        {actions.map((action) => (
          <button key={action.label} type="button" data-testid={action.testId} onClick={action.onClick}
            style={buttonStyle(action.kind, actionWidth)}
            onMouseEnter={action.kind === 'gold' ? (event) => { event.currentTarget.style.background = GOLD_DARK; } : undefined}
            onMouseLeave={action.kind === 'gold' ? (event) => { event.currentTarget.style.background = GOLD; } : undefined}>
            {action.kind === 'danger' ? <SmallTrashIcon /> : null}
            {action.label}
          </button>
        ))}
      </div>
    </div>
  </Dialog>
);

export default CmpDesignDialog;

/**
 * MD-K3 (ACC:1188-1196): CMP success pop-up. 520px, 76px green circle with a plain
 * check, 16px/600 text; it stays until the member closes it.
 */
export const CmpSuccessDialog = ({ open, onClose, message, testId }: {
  open: boolean; onClose: () => void; message: React.ReactNode; testId?: string;
}) => (
  <Dialog open={open} onClose={onClose} maxWidth={false}
    PaperProps={{ style: { borderRadius: 16, width: 'min(520px, 94vw)', margin: 0, boxShadow: '0 8px 30px rgba(0,0,0,.12)' } }}
    BackdropProps={{ style: { background: 'rgba(0,0,0,0.5)' } }}>
    <div data-testid={testId} role="status" style={{
      padding: '28px 36px 40px', boxSizing: 'border-box', position: 'relative', display: 'flex',
      flexDirection: 'column', alignItems: 'center', gap: 18, fontFamily: "'DM Sans', sans-serif",
    }}>
      <button type="button" onClick={onClose} aria-label="Close" style={{
        position: 'absolute', top: 14, right: 14, width: 28, height: 28, borderRadius: '50%', background: CMP_DESIGN_RED,
        color: '#FFFFFF', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 0,
      }}>✕</button>
      <div style={{ width: 76, height: 76, borderRadius: '50%', background: CMP_DESIGN_GREEN, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 8 }}>
        <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, color: '#000000', textAlign: 'center' }}>{message}</div>
    </div>
  </Dialog>
);
